import { useState } from 'react';
import { BarChart3, TrendingUp, TrendingDown, Calculator, Shield } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import {
  useSafetyKPITrends,
  useCalculateSafetyKPIs,
} from '@/hooks/use-h3comply';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';
import type { SafetyKPIPeriodType } from '@/lib/types';

// IRATA industry benchmark averages (configurable)
const BENCHMARKS = {
  ltifr: 0.87,
  trifr: 3.5,
  near_miss_rate: 15.0,
};

export function SafetyKPIDashboardPage() {
  const timeZone = useOrgTimeZone();
  const [periodType, setPeriodType] = useState<SafetyKPIPeriodType>('quarterly');
  const { data: trends, isLoading } = useSafetyKPITrends({ period_type: periodType });
  const calculateMutation = useCalculateSafetyKPIs();

  const [calcForm, setCalcForm] = useState({
    period_start: '',
    period_end: '',
    period_type: 'quarterly' as SafetyKPIPeriodType,
  });

  const handleCalculate = async () => {
    if (!calcForm.period_start || !calcForm.period_end) return;
    await calculateMutation.mutateAsync({
      period_start: calcForm.period_start,
      period_end: calcForm.period_end,
      period_type: calcForm.period_type,
    });
  };

  const chartData = (trends || []).map((kpi) => ({
    period: kpi.period_start,
    label: formatPeriodLabel(kpi.period_start, kpi.period_end, timeZone),
    LTIFR: Number(kpi.ltifr),
    TRIFR: Number(kpi.trifr),
    'Near Miss Rate': Number(kpi.near_miss_frequency_rate),
    'Toolbox Talk Rate': Number(kpi.toolbox_talk_rate),
  }));

  const latestKPI = trends && trends.length > 0 ? trends[trends.length - 1] : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Safety KPI Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            IRATA QP-300 Clause 8.1 — safety performance evaluation
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(['monthly', 'quarterly', 'annual'] as SafetyKPIPeriodType[]).map((pt) => (
            <button
              key={pt}
              onClick={() => setPeriodType(pt)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                periodType === pt
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {pt.charAt(0).toUpperCase() + pt.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Summary Cards */}
      {latestKPI && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KPICard
            title="LTIFR"
            value={Number(latestKPI.ltifr).toFixed(2)}
            benchmark={BENCHMARKS.ltifr}
            description="Lost Time Injury Frequency Rate"
            unit="per million hours"
          />
          <KPICard
            title="TRIFR"
            value={Number(latestKPI.trifr).toFixed(2)}
            benchmark={BENCHMARKS.trifr}
            description="Total Recordable Injury Frequency Rate"
            unit="per million hours"
          />
          <KPICard
            title="Near Miss Rate"
            value={Number(latestKPI.near_miss_frequency_rate).toFixed(2)}
            benchmark={BENCHMARKS.near_miss_rate}
            description="Near-miss frequency rate"
            unit="per million hours"
            invertComparison
          />
          <KPICard
            title="Toolbox Talk Rate"
            value={Number(latestKPI.toolbox_talk_rate).toFixed(2)}
            description="Toolbox talks per month"
            unit="per month"
          />
        </div>
      )}

      {/* Charts */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12 text-gray-500">Loading...</div>
      ) : chartData.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 p-12 text-center">
          <BarChart3 className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">No KPI data yet</h3>
          <p className="mt-2 text-sm text-gray-500">
            Calculate safety KPIs to see trends over time.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* LTIFR & TRIFR Chart */}
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="text-lg font-semibold text-gray-900">
              Injury Frequency Rates Over Time
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              LTIFR and TRIFR compared against IRATA industry averages
            </p>
            <div className="mt-4 h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <ReferenceLine
                    y={BENCHMARKS.ltifr}
                    stroke="#f59e0b"
                    strokeDasharray="5 5"
                    label={{ value: 'LTIFR Benchmark', position: 'insideTopRight', fontSize: 11 }}
                  />
                  <ReferenceLine
                    y={BENCHMARKS.trifr}
                    stroke="#ef4444"
                    strokeDasharray="5 5"
                    label={{ value: 'TRIFR Benchmark', position: 'insideTopRight', fontSize: 11 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="LTIFR"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="TRIFR"
                    stroke="#ef4444"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Near Miss & Toolbox Talk Rates */}
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="text-lg font-semibold text-gray-900">Leading Indicators</h2>
            <p className="mt-1 text-sm text-gray-500">
              Near-miss reporting and toolbox talk rates — proactive safety measures
            </p>
            <div className="mt-4 h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="Near Miss Rate"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="Toolbox Talk Rate"
                    stroke="#8b5cf6"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Leading vs Lagging Indicators */}
      {latestKPI && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-green-600" />
              <h2 className="text-lg font-semibold text-gray-900">Leading Indicators</h2>
            </div>
            <p className="mt-1 text-sm text-gray-500">Proactive safety measures</p>
            <div className="mt-4 space-y-3">
              {Object.entries(latestKPI.leading_indicators || {}).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between rounded-md bg-green-50 px-4 py-2">
                  <span className="text-sm text-gray-700">{formatIndicatorLabel(key)}</span>
                  <span className="text-sm font-semibold text-gray-900">{value}</span>
                </div>
              ))}
              {Object.keys(latestKPI.leading_indicators || {}).length === 0 && (
                <p className="text-sm text-gray-400">No leading indicator data available</p>
              )}
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-red-600" />
              <h2 className="text-lg font-semibold text-gray-900">Lagging Indicators</h2>
            </div>
            <p className="mt-1 text-sm text-gray-500">Reactive safety measures</p>
            <div className="mt-4 space-y-3">
              {Object.entries(latestKPI.lagging_indicators || {}).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between rounded-md bg-red-50 px-4 py-2">
                  <span className="text-sm text-gray-700">{formatIndicatorLabel(key)}</span>
                  <span className="text-sm font-semibold text-gray-900">{value}</span>
                </div>
              ))}
              {Object.keys(latestKPI.lagging_indicators || {}).length === 0 && (
                <p className="text-sm text-gray-400">No lagging indicator data available</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Calculate KPIs */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <div className="flex items-center gap-2">
          <Calculator className="h-5 w-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900">Calculate KPIs</h2>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          Auto-calculate safety KPIs for a specific period from system data
        </p>
        <div className="mt-4 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Period Start *</label>
            <input
              type="date"
              value={calcForm.period_start}
              onChange={(e) => setCalcForm((f) => ({ ...f, period_start: e.target.value }))}
              className="mt-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Period End *</label>
            <input
              type="date"
              value={calcForm.period_end}
              onChange={(e) => setCalcForm((f) => ({ ...f, period_end: e.target.value }))}
              className="mt-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Period Type</label>
            <select
              value={calcForm.period_type}
              onChange={(e) =>
                setCalcForm((f) => ({ ...f, period_type: e.target.value as SafetyKPIPeriodType }))
              }
              className="mt-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="annual">Annual</option>
            </select>
          </div>
          <button
            onClick={handleCalculate}
            disabled={calculateMutation.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            <Calculator className="h-4 w-4" />
            {calculateMutation.isPending ? 'Calculating...' : 'Calculate'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Helper Components ──────────────────────────────────────────────────────

function KPICard({
  title,
  value,
  benchmark,
  description,
  unit,
  invertComparison = false,
}: {
  title: string;
  value: string;
  benchmark?: number;
  description: string;
  unit: string;
  invertComparison?: boolean;
}) {
  const numValue = Number(value);
  const isAboveBenchmark = benchmark !== undefined && numValue > benchmark;
  const isBetter = invertComparison ? isAboveBenchmark : !isAboveBenchmark;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-gray-500">{title}</p>
        {benchmark !== undefined && (
          <div className={`flex items-center gap-1 text-xs ${isBetter ? 'text-green-600' : 'text-red-600'}`}>
            {isAboveBenchmark ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            {isAboveBenchmark ? 'Above' : 'Below'} avg
          </div>
        )}
      </div>
      <p className="mt-2 text-3xl font-bold text-gray-900">{value}</p>
      <p className="mt-1 text-xs text-gray-400">
        {unit}
        {benchmark !== undefined && ` (benchmark: ${benchmark})`}
      </p>
      <p className="mt-1 text-xs text-gray-500">{description}</p>
    </div>
  );
}

function formatPeriodLabel(start: string, end: string, timeZone: string): string {
  const monthOptions = { month: 'short' } as const;
  const startYear = start.slice(0, 4);
  const month = formatDateTimeInTimeZone(
    `${start.slice(0, 7)}-15T12:00:00Z`,
    timeZone,
    monthOptions
  );
  const endMonth = formatDateTimeInTimeZone(
    `${end.slice(0, 7)}-15T12:00:00Z`,
    timeZone,
    monthOptions
  );
  if (month === endMonth) return `${month} ${startYear}`;
  return `${month}-${endMonth} ${startYear}`;
}

function formatIndicatorLabel(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
